import { z } from 'zod';
import { objectIdSchema, optionalDate, optionalObjectId, optionalText, paginationQuerySchema, patchOf } from './common';
import { bpSchema, paiseSchema, positivePaiseSchema } from '../money';

export const QUOTATION_STATUSES = ['draft', 'approved', 'sent', 'accepted', 'rejected', 'expired', 'cancelled', 'superseded'] as const;
export type QuotationStatus = (typeof QUOTATION_STATUSES)[number];
/** Statuses in which the commercial terms can still change. */
export const QUOTATION_EDITABLE: QuotationStatus[] = ['draft'];

/** Quantity typed as a decimal (2.5 hours) stored as hundredths without floating-point drift. */
export const quantitySchema = z
  .number()
  .positive('Quantity must be greater than zero')
  .max(1_000_000)
  .refine((n) => /^\d+(\.\d{1,2})?$/.test(String(n)), 'Use at most 2 decimals')
  .transform((n) => {
    const [w, f = ''] = String(n).split('.');
    return Number(w) * 100 + Number(f.padEnd(2, '0'));
  });

export const quotationLineSchema = z.object({
  description: z.string().trim().min(1, 'Describe the item').max(500),
  /** Product / service code or HSN/SAC (optional). */
  code: optionalText(40),
  quantity: quantitySchema,
  unit: optionalText(20),
  unitPricePaise: paiseSchema,
  /** Line discount (absolute, before tax). */
  discountPaise: paiseSchema.default(0),
  /** GST / tax rate in basis points (18% = 1800). */
  taxRateBp: bpSchema.default(0),
});

export const paymentMilestoneSchema = z
  .object({
    title: z.string().trim().min(1).max(120),
    /** Either a share of the final amount (basis points) or a fixed amount. */
    shareBp: bpSchema.nullable().optional(),
    amountPaise: paiseSchema.nullable().optional(),
    dueDate: optionalDate(),
  })
  .refine((m) => (m.shareBp ?? null) !== null || (m.amountPaise ?? null) !== null, 'Give a share or an amount for each milestone');

const quotationBase = z.object({
  leadId: optionalObjectId(),
  clientId: optionalObjectId(),
  title: z.string().trim().min(2).max(160),
  description: optionalText(4000),
  validUntil: optionalDate(),
  lines: z.array(quotationLineSchema).min(1, 'Add at least one line item').max(100),
  /** Additional quotation-level discount (before tax), spread across lines. */
  discountPaise: paiseSchema.default(0),
  /** Expected value before commercial agreement (defaults to the lead's estimate). */
  estimatedValuePaise: paiseSchema.nullable().optional(),
  advancePaise: paiseSchema.nullable().optional(),
  milestones: z.array(paymentMilestoneSchema).max(24).default([]),
  paymentTerms: optionalText(2000),
  notes: optionalText(4000),
  /** Sales owner; defaults to the lead owner or the creator. */
  ownerId: optionalObjectId(),
});

export const quotationCreateSchema = quotationBase.refine((q) => !!(q.leadId || q.clientId), {
  message: 'A quotation must be linked to a lead or a client',
  path: ['leadId'],
});
export type QuotationCreateInput = z.input<typeof quotationCreateSchema>;

export const quotationUpdateSchema = patchOf(quotationBase.omit({ leadId: true, clientId: true }));
export type QuotationUpdateInput = z.input<typeof quotationUpdateSchema>;

export const quotationAcceptSchema = z.object({
  /** Amount the client accepted (defaults to the quoted total). */
  finalAgreedPaise: positivePaiseSchema.optional(),
  advancePaise: paiseSchema.nullable().optional(),
  acceptedAt: optionalDate(),
  note: optionalText(1000),
});
export type QuotationAcceptInput = z.input<typeof quotationAcceptSchema>;

export const quotationRejectSchema = z.object({ reason: z.string().trim().min(2).max(500) });

export const quotationListQuerySchema = paginationQuerySchema.extend({
  status: z.enum(QUOTATION_STATUSES).optional(),
  leadId: objectIdSchema.optional(),
  clientId: objectIdSchema.optional(),
  ownerId: z.union([objectIdSchema, z.literal('me')]).optional(),
});
export type QuotationListQuery = z.input<typeof quotationListQuerySchema>;
